import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import AnnouncementForm, {
	AnnouncementFormValues
} from "../../components/AnnouncementForm/AnnouncementForm";
import useCreateAnnouncement from "../../hooks/useCreateAnnouncement";
import useSendAnnouncement from "../../hooks/useSendAnnouncement";
import { adaptCreateAnnouncementPayload } from "../../adapters/adaptAnnouncementPayload";
import { formValuesToAnnouncement } from "../../adapters/formValuesToAnnouncement";

const EMPTY_DEFAULTS: AnnouncementFormValues = {
	notificationType: "push",
	title: "",
	body: "",
	heroImage: null,
	pushTitle: "",
	pushBody: "",
	audienceType: "everyone",
	audiencePlatform: "both",
	audienceUserIds: [],
	scheduleType: "now",
	scheduledDate: "",
	scheduledTime: "",
	hasExpiry: false,
	expiryDate: "",
	expiryTime: "",
	blockFiles: []
};

const CreateAnnouncement = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mutate: createAnnouncement, isLoading: isCreating } =
		useCreateAnnouncement();
	const { mutate: sendAnnouncement, isLoading: isSending } =
		useSendAnnouncement();
	const [showSendConfirm, setShowSendConfirm] = useState(false);
	const [pendingData, setPendingData] = useState<{
		data: AnnouncementFormValues;
		imageRemoved: boolean;
	} | null>(null);

	const handleSaveDraft = (
		data: AnnouncementFormValues,
		_imageRemoved: boolean
	) => {
		const announcementData = formValuesToAnnouncement(data);
		const payload = adaptCreateAnnouncementPayload(
			announcementData,
			data.heroImage,
			data.blockFiles
		);
		createAnnouncement(payload, {
			onSuccess: async () => {
				await queryClient.invalidateQueries(["announcements"]);
				navigate("/announcements");
			}
		});
	};

	const handleSendOrSchedule = (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => {
		setPendingData({ data, imageRemoved });
		setShowSendConfirm(true);
	};

	const confirmSend = () => {
		if (!pendingData) return;
		const { data } = pendingData;
		const announcementData = formValuesToAnnouncement(data);
		const payload = adaptCreateAnnouncementPayload(
			announcementData,
			data.heroImage,
			data.blockFiles
		);
		createAnnouncement(payload, {
			onSuccess: (created) => {
				sendAnnouncement(created.id, {
					onSuccess: async () => {
						setShowSendConfirm(false);
						await queryClient.invalidateQueries(["announcements"]);
						navigate("/announcements");
					},
					onError: () => {
						setShowSendConfirm(false);
					}
				});
			},
			onError: () => {
				setShowSendConfirm(false);
			}
		});
	};

	const getConfirmMessage = () => {
		if (!pendingData) return "";
		const { data } = pendingData;
		const audience =
			data.audienceType === "everyone"
				? "all users"
				: data.audienceType === "platform"
					? `${data.audiencePlatform === "both" ? "all" : data.audiencePlatform} users`
					: `${data.audienceUserIds.length} selected user${data.audienceUserIds.length !== 1 ? "s" : ""}`;

		if (data.scheduleType === "scheduled") {
			return `Schedule this announcement for ${data.scheduledDate} at ${data.scheduledTime || "00:00"} to ${audience}?`;
		}
		return `Send this announcement now to ${audience}?`;
	};

	return (
		<Page>
			<AnnouncementForm
				defaultValues={EMPTY_DEFAULTS}
				onSubmit={handleSaveDraft}
				onSendOrSchedule={handleSendOrSchedule}
				isSubmitting={isCreating}
				isSending={isSending}
			/>
			<ConfirmationModal
				isOpen={showSendConfirm}
				title={
					pendingData?.data.scheduleType === "scheduled"
						? "Schedule announcement?"
						: "Send announcement?"
				}
				description={getConfirmMessage()}
				onCancel={() => {
					if (!isCreating) setShowSendConfirm(false);
				}}
				cancelBtnProps={{ disabled: isCreating || isSending }}
				onSubmit={confirmSend}
				submitBtnProps={{ isLoading: isCreating || isSending }}
			/>
		</Page>
	);
};

export default CreateAnnouncement;
